import { backendApi } from "./backend.api";
import { IMachine } from "shared/models/models";
import { getSpaceId } from "shared/utils/space";

export interface IMachineListQuery {
  title?: string;
  serial?: string;
  equalSerial?: boolean;
  provider?: number;
  address?: number;
  spaceId?: number;
  ids?: number[];
  orderBy?: string;
}

interface IMachineTitlesQuery {
  spaceId?: number;
}

export const machineApi = backendApi.injectEndpoints({
  endpoints: (build) => {
    return {
      machines: build.query<IMachine[], IMachineListQuery>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IMachineListQuery = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/machine",
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Machine" as const, id })),
                "Machine",
              ]
            : ["Machine"],
      }),
      getMachineTitles: build.query<string[], null>({
        query: () => {
          const spaceId = getSpaceId();
          const params: IMachineTitlesQuery = {};
          if (spaceId) params.spaceId = spaceId;
          return {
            url: "/machine/titles",
            method: "GET",
            params: params,
          };
        },
      }),
      getMachine: build.query<IMachine, number>({
        query: (id) => ({ url: `/machine/${id}`, method: "GET" }),
        transformErrorResponse: (response: { route: string | number }) =>
          response.route,
        providesTags: (item) => [{ type: "Machine", id: item?.id }],
      }),
      addMachine: build.mutation<IMachine, Partial<IMachine>>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: Partial<IMachine> = { ...payload };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/machine",
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: () => ["Machine"],
        transformResponse: (response: { data: IMachine }) => response.data,
      }),
      updateMachine: build.mutation<IMachine, Partial<IMachine>>({
        query: (payload) => ({
          url: `/machine/${payload.id}`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [{ type: "Machine", id: item?.id }],
        //transformResponse: (result, error) => [{ type: 'Visit', id: result?.id }],
      }),
      updateMultiMachine: build.mutation<IMachine[], IMachine[]>({
        query: (payload) => ({
          url: "/machine/update-multi",
          method: "POST",
          data: payload,
        }),
        invalidatesTags: (result = []) => [
          ...result.map(({ id }) => ({ type: "Machine", id: id }) as const),
        ],
        // transformResponse: (response: { data: IVisit[] }) => response.data,
      }),
      deleteMachine: build.mutation<IMachine, number>({
        query: (id) => ({
          url: `/machine/${id}`,
          method: "DELETE",
          credentials: "include",
        }),
        invalidatesTags: ["Machine"],
      }),
    };
  },
});

export const {
  useMachinesQuery,
  useAddMachineMutation,
  useDeleteMachineMutation,
  useGetMachineQuery,
  useUpdateMachineMutation,
  useGetMachineTitlesQuery,
  useUpdateMultiMachineMutation,
} = machineApi;
